import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useTracking } from '@/composables/useTracking';

export interface PagePerformanceData {
  trigger_ts: number;           // 毫秒时间戳 (long)
  page_path: string;            // 页面路径 (string)
  init_time?: number;           // Runtime能统计到的最早时间点 (int，毫秒)
  page_create_time?: number;    // 页面创建时间 (int，毫秒)
  main_request_start_time?: number; // 主要请求开始时间 (int，毫秒)
  main_request_end_time?: number;   // 主要请求结束时间 (int，毫秒)
  page_on_ready?: number;       // 页面初次渲染完成时间 (int，毫秒)
  page_interactive_time?: number; // 页面可交互时间 (int，毫秒)
  type: string;                 // H5/FinClip/Android/iOS/Flutter (string)
  first_contentful_paint_time?: number; // H5-首次内容绘制时间 (FCP) (int，毫秒)
  largest_contentful_paint_time?: number; // H5-最大内容绘制时间 (LCP) (int，毫秒)
}

export function usePagePerformance() {
  const route = useRoute();
  const { track } = useTracking();
  
  // 性能时间节点
  const performanceTiming = ref<PagePerformanceData>({
    trigger_ts: 0,
    page_path: '',
    type: 'H5'
  });

  // 时间记录器
  const timingRecorder = ref<Record<string, number>>({});
  
  // 获取trigger_ts参数
  const getTriggerTs = (): number => {
    const urlParams = new URLSearchParams(window.location.search);
    const triggerTs = urlParams.get('trigger_ts');
    return triggerTs ? parseInt(triggerTs) : Date.now();
  };

  // 获取当前页面路径
  const getCurrentPagePath = (): string => {
    return route.path || window.location.pathname || '/';
  };

  // 计算时间差（毫秒为单位）
  const calculateTimeDiff = (startTime: number, endTime: number): number => {
    return endTime - startTime;
  };

  // 记录时间节点
  const recordTiming = (name: string) => {
    timingRecorder.value[name] = Date.now();
  };

  // 获取时间差
  const getTimeDiff = (name: string): number => {
    const triggerTime = performanceTiming.value.trigger_ts;
    const currentTime = timingRecorder.value[name];
    if (!currentTime) return 0;
    return calculateTimeDiff(triggerTime, currentTime);
  };

  // 初始化性能监控
  const initPerformanceMonitoring = () => {
    const triggerTs = getTriggerTs();
    const pagePath = getCurrentPagePath();
    
    performanceTiming.value = {
      trigger_ts: triggerTs,
      page_path: pagePath,
      type: 'H5'
    };

    recordTiming('init');
    performanceTiming.value.init_time = getTimeDiff('init');
  };

  // 记录页面创建时间
  const recordPageCreate = () => {
    recordTiming('page_create');
    performanceTiming.value.page_create_time = getTimeDiff('page_create');
  };

  // 记录主要请求开始时间
  const recordMainRequestStart = () => {
    recordTiming('main_request_start');
    performanceTiming.value.main_request_start_time = getTimeDiff('main_request_start');
  };

  // 记录主要请求结束时间
  const recordMainRequestEnd = () => {
    recordTiming('main_request_end');
    performanceTiming.value.main_request_end_time = getTimeDiff('main_request_end');
  };

  // 记录页面渲染完成时间
  const recordPageReady = () => {
    recordTiming('page_ready');
    performanceTiming.value.page_on_ready = getTimeDiff('page_ready');
  };

  // 记录页面可交互时间
  const recordPageInteractive = () => {
    recordTiming('page_interactive');
    performanceTiming.value.page_interactive_time = getTimeDiff('page_interactive');
  };

  // 记录首次内容绘制时间 (FCP)
  const recordFirstContentfulPaint = (fcpTime: number) => {
    const triggerTime = performanceTiming.value.trigger_ts;
    const pageLoadTime = performance.timing?.navigationStart || performance.timeOrigin;
    const timeFromPageLoadToTrigger = triggerTime - pageLoadTime;
    
    const fcpDiff = (timeFromPageLoadToTrigger < 0 || timeFromPageLoadToTrigger > 60000) 
      ? fcpTime 
      : fcpTime + timeFromPageLoadToTrigger;
    
    performanceTiming.value.first_contentful_paint_time = fcpDiff;
  };

  // 记录最大内容绘制时间 (LCP)
  const recordLargestContentfulPaint = (lcpTime: number) => {
    const triggerTime = performanceTiming.value.trigger_ts;
    const pageLoadTime = performance.timing?.navigationStart || performance.timeOrigin;
    const timeFromPageLoadToTrigger = triggerTime - pageLoadTime;
    
    const lcpDiff = (timeFromPageLoadToTrigger < 0 || timeFromPageLoadToTrigger > 60000) 
      ? lcpTime 
      : lcpTime + timeFromPageLoadToTrigger;
    
    performanceTiming.value.largest_contentful_paint_time = lcpDiff;
  };

  // 发送性能数据
  const sendPerformanceData = () => {
    updatePagePath();
    track('tech_page_open', performanceTiming.value);
  };

  // 监听页面可交互状态
  const checkPageInteractive = () => {
    // 使用 requestIdleCallback 或 setTimeout 来检测页面可交互
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        recordPageInteractive();
      });
    } else {
      setTimeout(() => {
        recordPageInteractive();
      }, 100);
    }
  };

  // 监听DOM内容加载完成
  const onDOMContentLoaded = () => {
    recordPageReady();
    checkPageInteractive();
  };

  // 监听页面完全加载
  const onPageLoad = () => {
    // 页面完全加载后的额外处理
    setTimeout(() => {
      sendPerformanceData();
    }, 100);
  };

  // 设置容器类型
  const setContainerType = (type: 'H5' | 'FinClip' | 'Android' | 'iOS' | 'Flutter') => {
    performanceTiming.value.type = type;
  };

  // 更新页面路径
  const updatePagePath = () => {
    const newPagePath = getCurrentPagePath();
    if (newPagePath !== performanceTiming.value.page_path) {
      performanceTiming.value.page_path = newPagePath;
    }
  };

  // 监听路由变化
  watch(() => route.path, updatePagePath);

  return {
    // 状态
    performanceTiming,
    timingRecorder,
    
    // 初始化
    initPerformanceMonitoring,
    
    // 时间记录方法
    recordPageCreate,
    recordMainRequestStart,
    recordMainRequestEnd,
    recordPageReady,
    recordPageInteractive,
    recordFirstContentfulPaint,
    recordLargestContentfulPaint,
    
    // 事件监听
    onDOMContentLoaded,
    onPageLoad,
    
    // 配置
    setContainerType,
    updatePagePath,
    
    // 数据发送
    sendPerformanceData
  };
}
